/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  ActionIcon, Card, CircularGaugeChart, DetailStack, Dropdown, HStack, ICON_EMAIL_OUTLINE, ICON_EYE_OUTLINE,
  ICON_PHONE_OUTLINE,
  ICON_WHATSAPP,
  Popover,
  Spacing,
  Stack,
  UserPreviewSkeleton,
} from '@doveit/bricks';
import { formatEuro, pluralizeWord } from '@doveit/hammer';
import { SerpAgent } from '../../../providers/api/dtos';
import { jobTitleLabels } from '../../../labels';
import { ViewAgentSmallPreview } from '../../components';
import { toAgentPreviewProps } from '../../mappers/toAgentPreviewProps';
import ViewAgentPopoverPreview from '../view-agent-popover-preview/ViewAgentPopoverPreview';
import { ReferenceType } from '../../../domain/types';
import { goToDetailPage } from '../../../utils/navigate/utils';
import SendWhatsappAction from '../../../containers/send-whatsapp-action/SendWhatsappAction';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import { useAgent } from '../../../hooks/use-agent/useAgent';
import AgentRankPosition from '../../components/agent-rank-position/AgentRankPosition';
import { configByPerformanceRating } from '../../constants';
import ProvincePopover from '../../components/province-popover/ProvincePopover';
import * as styles from './ViewSerpAgentCard.style';
import AgencyPopover from '../../components/agency-popover/AgencyPopover';

export interface ViewSerpAgentCardProps {
  serpAgent: SerpAgent,
  canViewAgentPage?: boolean,
  canViewAgentRank?: boolean,
  canViewAgentGoal?: boolean,
}

const ViewSerpAgentCard: React.FC<ViewSerpAgentCardProps> = ({
  serpAgent,
  canViewAgentPage = false,
  canViewAgentRank = false,
  canViewAgentGoal = false,
}) => {
  const { agent, rank, goal } = serpAgent;
  const { data: areaManager, isLoading: isAreaManagerLoading } = useAgent(agent.areaManagerId);
  const { data: parent, isLoading: isParentLoading } = useAgent(agent.idParent);

  const goToAgentPage = React.useCallback(() => {
    goToDetailPage(agent.id!, ReferenceType.AGENT);
  }, [agent]);

  return (
    <Card>
      <Card.Header
        primaryActions={[canViewAgentPage && (
          <ActionIcon
            aria-label="Visualizza agente"
            label="Visualizza"
            size="S"
            icon={{ path: ICON_EYE_OUTLINE }}
            onClick={goToAgentPage}
          />
        )]}
        secondaryActions={[
          ...(agent.mobile.work ? (
            [
              <Dropdown.Option
                label="Chiama agente"
                aria-label="Chiama l'agente"
                iconLeft={{ path: ICON_PHONE_OUTLINE }}
                href={`tel:${agent.mobile.work}`}
              />,
              <SendWhatsappAction
                phoneNumber={agent.mobile.work}
              >
                {({ onClick }) => (
                  <Dropdown.Option
                    label="Invia WhatsApp"
                    aria-label="Invia un messaggio WhatsApp all'agente"
                    iconLeft={{ path: ICON_WHATSAPP }}
                    onClick={onClick}
                  />
                )}
              </SendWhatsappAction>,
            ]
          ) : []),
          agent.email.work && (
            <Dropdown.Option
              label="Invia email"
              aria-label="Invia un'email all'agente"
              iconLeft={{ path: ICON_EMAIL_OUTLINE }}
              href={`mailto:${agent.email.work}`}
            />
          ),
        ]}
      >
        <ViewAgentPopoverPreview
          agent={agent}
          trigger={(
            <ViewAgentSmallPreview
              {...toAgentPreviewProps(agent)}
              details={`${jobTitleLabels[agent.jobTitle]}${agent.isRookie ? ' (Rookie)' : ''}`}
              size="S"
              expanded
            />
          )}
        />
      </Card.Header>
      <Card.Content>
        <Stack gap={150}>
          <ProvincePopover
            coveredGeo={agent.coveredGeo}
            trigger={(
              <DetailStack
                label={`${agent.coveredGeo?.length || 0} ${pluralizeWord('provinc', 'ia', 'e', agent.coveredGeo?.length || 0)}`}
                aria-label="Province coperte dall'agente"
              >
                <div css={setVerticalEllipsis(1)}>
                  {(agent.coveredGeo && agent.coveredGeo?.length > 0)
                    ? agent.coveredGeo?.map(({ province, weight }) => `${province} (${weight * 100}%)`).join(', ')
                    : NO_VALUE_SYMBOL}
                </div>
              </DetailStack>
            )}
          />
          <AgencyPopover
            agencies={agent.agencies}
            trigger={(
              <DetailStack
                label={`${agent.agencies?.length || 0} ${pluralizeWord('agenzi', 'a', 'e', agent.agencies?.length || 0)}`}
                aria-label="Agenzie dove opera l'agente"
              >
                <div css={setVerticalEllipsis(1)}>
                  {(agent.agencies && agent.agencies?.length > 0)
                    ? agent.agencies?.map(({ shortName }) => shortName).join(', ')
                    : NO_VALUE_SYMBOL}
                </div>
              </DetailStack>
            )}
          />

          <HStack gap={150}>
            {(isAreaManagerLoading || isParentLoading) && (
              <UserPreviewSkeleton size="S" />
            )}

            {(agent.areaManagerId === agent.idParent) && areaManager && (
              <ViewAgentPopoverPreview
                agent={areaManager}
                trigger={(
                  <ViewAgentSmallPreview
                    {...toAgentPreviewProps(areaManager, { omit: { name: true } })}
                    size="S"
                    expanded
                  />
                )}
              />
            )}

            {(agent.areaManagerId !== agent.idParent) && areaManager && parent && (
              <Popover
                trigger={(
                  <div css={styles.areaManagerParentPreviewsWrapper}>
                    <ViewAgentSmallPreview
                      {...toAgentPreviewProps(areaManager, { omit: { name: true } })}
                      size="S"
                    />
                    <ViewAgentSmallPreview
                      {...toAgentPreviewProps(parent, { omit: { name: true } })}
                      size="S"
                    />
                  </div>
                )}
                hasArrow
              >
                <Spacing padding={150}>
                  <Stack gap={100}>
                    <ViewAgentSmallPreview
                      {...toAgentPreviewProps(areaManager)}
                      details="Area Manager"
                      size="S"
                    />
                    <ViewAgentSmallPreview
                      {...toAgentPreviewProps(parent)}
                      details="Gestore"
                      size="S"
                    />
                  </Stack>
                </Spacing>
              </Popover>
            )}
          </HStack>

          {((rank && canViewAgentRank) || (goal && canViewAgentGoal)) && (
            <>
              <Card.Divider />
              <HStack gap={150} aria-label="Info extra">
                {rank && canViewAgentRank && (
                  <AgentRankPosition
                    rank={rank}
                    rankingType="AGENT"
                    size="S"
                  />
                )}
                {goal && canViewAgentGoal && (
                  <HStack gap={100} wrap="nowrap">
                    <CircularGaugeChart
                      progress={goal.progress}
                      size="S"
                      color={configByPerformanceRating[goal.status].color}
                      hideValue
                    />
                    <DetailStack
                      label="Obiettivo"
                      aria-label="Obiettivo"
                    >
                      {formatEuro(goal?.target)}
                    </DetailStack>
                  </HStack>
                )}
              </HStack>
            </>
          )}
        </Stack>
      </Card.Content>
    </Card>
  );
};

export default ViewSerpAgentCard;
